import { Channel } from '@/Enums/Channel.enum';
import { ChannelId } from '@/Enums/ChannelId.enum';
import ForecasterDateRange from '@/Enums/Forecaster/DateRange.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import { RON_PRODUCT, isGamChannel, isGamChannelId } from '@/constants/forecaster';
import moment from 'moment';
import { getControlOptionsValues } from '../filters/filterMapping';
import { dateRangeToFormat, dateToFormat } from '../valueFormatter/dateFormatter.helpers';

export const CHANNEL_ID_MAP = {
  [Channel.CTV]: ChannelId.CTV,
  [Channel.OLV]: ChannelId.OLV,
  [Channel.Display]: ChannelId.Display,
  [Channel.SpotifyDirect]: ChannelId.SpotifyDirect,
  [Channel.Google]: ChannelId.Google,
  [Channel.Amazon]: ChannelId.Amazon,
  [Channel.Dooh]: ChannelId.Dooh,
  [Channel.Gaming]: ChannelId.Gaming,
  [Channel.Audio]: ChannelId.Audio,
  [Channel.GamVideo]: ChannelId.GamVideo,
  [Channel.GamDisplay]: ChannelId.GamDisplay,
};

const CHANNEL_NAME_BY_ID = Object.fromEntries(Object.entries(CHANNEL_ID_MAP).map(([name, id]) => [id, name]));

const getChannelKeyFromPayload = data => {
  if (!data) return null;
  if (data.name) return data.name;
  if (data.channelId == null) return null;
  return CHANNEL_NAME_BY_ID[data.channelId] ?? CHANNEL_NAME_BY_ID[+data.channelId] ?? null;
};

export const dateToRangeFormat = (firstDate, secondDate) => {
  if (secondDate) {
    return dateRangeToFormat([firstDate, secondDate]);
  } else {
    return dateToFormat(firstDate, DateFormat.DayShortMonthYear);
  }
};

const coerceBoolean = value => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0' || normalized === '') return false;
  }
  return Boolean(value);
};

// #region getForecastedImpressions
// TODO: convert to ts
export const mapValueForChannel = currentChannel => {
  if (currentChannel?.channelId === ChannelId.Dooh) {
    return {
      channelId: currentChannel.channelId,
      doohPublishers: [],
      mimeTypes: currentChannel.mimeTypes && [currentChannel.mimeTypes],
      isCustomList: coerceBoolean(currentChannel?.isCustomList),
      venueIds: currentChannel.venueIds,
    };
  }

  if (isGamChannelId(currentChannel?.channelId)) {
    const topAdUnitId = currentChannel.gamTopAdUnitId;
    // RON → omit ad-unit fields (whole-network forecast); the 'RON' sentinel would fail the numeric validator.
    const isRon = !topAdUnitId || topAdUnitId === RON_PRODUCT.id;
    return {
      channelId: currentChannel.channelId,
      isCustomList: false,
      ...(isRon
        ? {}
        : {
            gamTopAdUnitId: topAdUnitId,
            gamSubAdUnitIds: currentChannel.gamSubAdUnitIds ?? [],
          }),
      videoPositions: currentChannel.videoPositions ?? [],
      creativeSizes: currentChannel.creativeSizes ?? [],
    };
  }

  const {
    selectedAudience,
    selectedAudienceBlocks,
    selectedAdswizzAudience,
    selectedProduct,
    selectedAdswizzProduct,
    channelId,
    isCustomList,
    isAverageToggled,
    averageCpm,
    duration,
    dealGroups,
    packageId,
    ...otherFilters
  } = currentChannel ?? {};

  const cpmOptions = isAverageToggled ? { averageCpm } : {};

  const dataToRequest = {
    channelId,
    isCustomList: coerceBoolean(isCustomList),
    duration,
    audience: AudienceTargetingService.audienceControlOptionsToTargeting(selectedAudience),
    audienceBlocks: AudienceTargetingService.blocksToRequest(selectedAudienceBlocks),
    adswizzAudience: AudienceTargetingService.audienceControlOptionsToTargeting(selectedAdswizzAudience),
    ...cpmOptions,
  };

  if (selectedProduct && selectedProduct !== RON_PRODUCT.id) {
    dataToRequest.productId = selectedProduct;
    dataToRequest.beeswaxPublishers = Object.entries(otherFilters[`selectedProduct_${selectedProduct}_pubs`] ?? {})
      ?.filter(([, value]) => !!value)
      ?.map(([key]) => +key);
  }

  if (dealGroups?.length) {
    dataToRequest.dealGroupIds = getControlOptionsValues(dealGroups);
  }

  if (packageId) {
    dataToRequest.packageId = packageId;
  }

  if (selectedAdswizzProduct) {
    dataToRequest.adswizzProductId = selectedAdswizzProduct;
  }

  return dataToRequest;
};
// #endregion

// #region save forecast
const DOOH_CHANNEL_HYDRATOR = channelData => {
  const doohChannel = {};
  doohChannel.doohPublishers = [];
  const { mimeTypes } = channelData;
  doohChannel.mimeTypes = mimeTypes ? [mimeTypes] : null;
  doohChannel.venueIds = channelData.venueIds ?? null;

  return doohChannel;
};

const AUDIO_CHANNEL_HYDRATOR = channelData => {
  const { selectedAdswizzProduct, selectedAdswizzAudience } = channelData;

  const audioChannel = {
    adswizzAudience: AudienceTargetingService.audienceControlOptionsToTargeting(selectedAdswizzAudience),
  };

  if (selectedAdswizzProduct) {
    audioChannel.adswizzProductId = selectedAdswizzProduct;
  }

  return audioChannel;
};

const getAudienceToSave = channelData => ({
  audience: AudienceTargetingService.audienceControlOptionsToTargeting(channelData.selectedAudience),
  audienceBlocks: AudienceTargetingService.blocksToSave(channelData.selectedAudienceBlocks),
});

const CTV_CHANNEL_HYDRATOR = channelData => {
  const { selectedProduct } = channelData;

  const ctvChannel = getAudienceToSave(channelData);

  if (selectedProduct) {
    ctvChannel.productId = selectedProduct;
  }

  const selectedPublishersName = `selectedProduct_${selectedProduct}_pubs`;
  const selectedPublishers = channelData[selectedPublishersName]?.map((item, index) => item && index).filter(Boolean);
  ctvChannel.beeswaxPublishers = selectedPublishers ? selectedPublishers : null;

  return ctvChannel;
};

const GAM_CHANNEL_HYDRATOR = channelData => {
  const { gamTopAdUnitId, gamSubAdUnitIds, videoPositions, creativeSizes } = channelData;
  // RON → omit ad-unit fields (whole-network), same as the preview mapping.
  const isRon = !gamTopAdUnitId || gamTopAdUnitId === RON_PRODUCT.id;
  return {
    ...(isRon ? {} : { gamTopAdUnitId, gamSubAdUnitIds: gamSubAdUnitIds ?? [] }),
    videoPositions: videoPositions ?? [],
    creativeSizes: creativeSizes ?? [],
  };
};

const defaultChannelHydrator = channelData => getAudienceToSave(channelData);

const CHANNEL_DATA_HYDRATORS = {
  [ChannelId.Dooh]: DOOH_CHANNEL_HYDRATOR,
  [ChannelId.SpotifyDirect]: AUDIO_CHANNEL_HYDRATOR,
  [ChannelId.CTV]: CTV_CHANNEL_HYDRATOR,
  [ChannelId.GamVideo]: GAM_CHANNEL_HYDRATOR,
  [ChannelId.GamDisplay]: GAM_CHANNEL_HYDRATOR,
};

export const mapDataToSend = (data, channels) => {
  const isOldRecord = data.forecastName === data.forecastNameInDB;
  const preparedChannels = Object.values(channels)
    .map(channel => {
      const channelId = CHANNEL_ID_MAP[channel];
      const currentData = data[channel] ?? data[channelId];
      if (!currentData) {
        return null;
      }
      const readyChannel = {
        isMainChannel: data.currentChannel === channel || data.currentChannel === channelId,
        channelId: currentData?.channelId ?? channelId,
        tabIndex: data.tabIndex,
        isCustomList: coerceBoolean(currentData?.isCustomList),
        isAverageToggled: coerceBoolean(currentData?.isAverageToggled),
        averageCpm: currentData?.averageCpm,
        duration: currentData?.duration,
        dealGroupIds: getControlOptionsValues(currentData?.dealGroups ?? []),
        packageId: currentData?.packageId ?? null,
      };

      if (
        currentData?.selectedProduct &&
        currentData?.selectedProduct !== RON_PRODUCT.id &&
        currentData?.selectedProduct > 0
      ) {
        readyChannel.productId = currentData.selectedProduct;
      }

      if (isOldRecord) readyChannel.id = currentData.id;

      const channelHydrationFunction = CHANNEL_DATA_HYDRATORS[currentData.channelId] ?? defaultChannelHydrator;

      return { ...readyChannel, ...channelHydrationFunction?.(currentData) };
    })
    .filter(Boolean);

  const dataToSend = {
    dateRange: data.dateRange,
    forecastName: data.forecastName,
    tabIndex: data.tabIndex,
    geographic: data.geographic,
    geoExclusion: data.geoExclusion,
    daysInRange: data.daysInRange,
    isGeoFencingSearch: coerceBoolean(data.isGeoFencingSearch),
    geoFence: data.geoFence,
    channels: preparedChannels,
  };
  if (data.frequencyCapEnabled) {
    dataToSend.frequencyCapPerDay = data.frequencyCapPerDay;
  }
  if (isOldRecord) {
    dataToSend.forecastId = data.forecastId;
  }
  return dataToSend;
};
// #endregion

export const getDefaultChannelFilters = (channel, products = []) => {
  if (channel === Channel.Dooh) {
    return {
      channelId: ChannelId.Dooh,
      mimeTypes: null,
      venueIds: [],
      doohPublishers: [],
    };
  }

  if (isGamChannel(channel)) {
    return {
      channelId: CHANNEL_ID_MAP[channel],
      isCustomList: false,
      gamTopAdUnitId: RON_PRODUCT.id,
      gamSubAdUnitIds: [],
      videoPositions: [],
      creativeSizes: [],
      calculator: {
        impressions: null,
        cpm: null,
        budget: null,
      },
    };
  }
  const filter = {
    channelId: CHANNEL_ID_MAP[channel],
    isCustomList: false,
    selectedAudience: AudienceTargetingService.defaultAudienceTargeting,
    selectedAudienceBlocks: [],
    selectedAdswizzAudience: AudienceTargetingService.defaultAudienceTargeting,
    selectedAdswizzProduct: '',
    isAverageToggled: false,
    averageCpm: 0,
    duration: null,
    dealGroupIds: [],
    packageId: null,
    calculator: {
      impressions: null,
      cpm: null,
      budget: null,
    },
  };
  if (channel === Channel.Google) {
    filter.selectedProduct = products.find(product => product.isDefault)?.id;
  }

  return filter;
};

export const getCurrentChannelProducts = (productList = {}, channel) => {
  if (productList[channel]?.length) {
    return productList[channel];
  }
  return [RON_PRODUCT];
};

export const mapDataToLoad = (data, products = {}) => {
  const channelsRaw = data.forecastChannels ?? [];
  const normalizedChannels = channelsRaw.map(channel => {
    const channelId = channel.channelId ?? channel.channel_id;
    return {
      ...channel,
      channelId,
      name: channel.name ?? channel.channel?.name ?? CHANNEL_NAME_BY_ID[channelId],
      isMainChannel: channel.isMainChannel ?? channel.is_main_channel,
      tabIndex: channel.tabIndex ?? channel.tab_index,
    };
  });

  const channels = Object.fromEntries(
    normalizedChannels.filter(channel => channel?.channelId != null).map(c => [c.channelId, c]),
  );

  const mainChannel = normalizedChannels.find(c => c.isMainChannel);
  const tabIndex = mainChannel?.tabIndex ?? data.tabIndex ?? data.tab_index ?? 0;

  const mappedChannels = Object.entries(CHANNEL_ID_MAP).reduce((acc, [channel, channelId]) => {
    const currentChannel = channels[channelId];

    if (currentChannel) {
      acc[channel] = {
        id: currentChannel.id,
        channelId,
        isCustomList: coerceBoolean(currentChannel.isCustomList ?? currentChannel.is_custom_list),
        selectedAudience: AudienceTargetingService.audienceMetaTargetingToAudienceControlOptionTargeting(
          currentChannel.audienceMeta,
        ),
        selectedAudienceBlocks: AudienceTargetingService.blocksMetaToBlocks(currentChannel.audienceBlocksMeta),
        selectedProduct: currentChannel.product ? currentChannel.product.id.toString() : null,
        isAverageToggled: coerceBoolean(currentChannel.isAverageToggled ?? currentChannel.is_average_toggled),
        averageCpm: currentChannel.averageCpm ?? currentChannel.average_cpm,
        duration: currentChannel.duration,
        dealGroups: (currentChannel.dealGroupIds ?? currentChannel.deal_group_ids ?? []).map(dealGroupId => ({
          value: dealGroupId,
          label: '',
        })),
        packageId: currentChannel.packageId ?? currentChannel.package_id ?? null,
      };
    } else {
      acc[channel] = getDefaultChannelFilters(channel);
    }

    if (channelId === ChannelId.CTV && currentChannel && acc[channel].selectedProduct) {
      const selectedPublishersName = `selectedProduct_${acc[channel].selectedProduct}_pubs`;
      acc[channel][selectedPublishersName] = [];
      const beeswaxPublishers = currentChannel.beeswaxPublishers ?? currentChannel.beeswax_publishers ?? [];
      beeswaxPublishers.forEach(publisher => (acc[channel][selectedPublishersName][publisher] = true));
    }

    if (channelId === ChannelId.SpotifyDirect) {
      acc[channel].selectedAdswizzAudience =
        AudienceTargetingService.adswizzAudienceMetaTargetingToAudienceControlOptionTargeting(
          currentChannel?.adswizzAudienceMeta,
        );
      acc[channel].selectedAdswizzProduct = currentChannel?.adswizzProduct?.id?.toString();
    }

    if (channelId === ChannelId.Dooh && currentChannel) {
      const mimeTypes = currentChannel.mimeTypes ?? currentChannel.mime_types;
      acc[channel].mimeTypes = mimeTypes ? mimeTypes[0] : null;
      acc[channel].doohPublishers = [];
      acc[channel].venueIds = currentChannel.venueIds ?? currentChannel.venue_ids;
    }

    if (channelId === ChannelId.Google) {
      acc[channel].selectedProduct = (products?.[channel] ?? []).find(product => product.isDefault)?.id;
    }

    if (isGamChannelId(channelId) && currentChannel) {
      // null top → Run of Network (default sentinel).
      acc[channel].gamTopAdUnitId =
        currentChannel.gamTopAdUnitId ?? currentChannel.gam_top_ad_unit_id ?? RON_PRODUCT.id;
      acc[channel].gamSubAdUnitIds = currentChannel.gamSubAdUnitIds ?? currentChannel.gam_sub_ad_unit_ids ?? [];
      acc[channel].videoPositions = currentChannel.videoPositions ?? currentChannel.video_positions ?? [];
      acc[channel].creativeSizes = currentChannel.creativeSizes ?? currentChannel.creative_sizes ?? [];
    }

    return acc;
  }, {});

  const calcDaysInRange = (ti, dr) => {
    switch (ti) {
      case 0:
        return ForecasterDateRange.Week;
      case 1:
        return ForecasterDateRange.Day;
      case 2:
        return ForecasterDateRange.Month;
      case 3: {
        const dateRange = dr ?? '';
        const dates = dateRange.split(' - ');
        if (!dates[1]) {
          return 1;
        }
        const start = moment(dates[0], DateFormat.DayShortMonthYear);
        const end = moment(dates[1], DateFormat.DayShortMonthYear);

        return end.diff(start, 'days') + 1;
      }
      default:
        break;
    }
  };

  const dateRange = data.dateRange ?? data.date_range;

  return {
    forecastName: data.forecastName ?? data.forecast_name,
    forecastNameInDB: data.forecastName ?? data.forecast_name,
    forecastId: data.id,
    tabIndex,
    currentChannel:
      mainChannel?.name ??
      CHANNEL_NAME_BY_ID[mainChannel?.channelId] ??
      data.name ??
      CHANNEL_NAME_BY_ID[data.channelId ?? data.channel_id] ??
      Channel.CTV,
    daysInRange: calcDaysInRange(tabIndex, dateRange),
    ...mappedChannels,
    geographic: data.geographic,
    geoExclusion: data.geoExclusion ?? data.geo_exclusion,
    geoFence: data.geoFence ?? data.geo_fence,
    dateRange,
    isGeoFencingSearch: false,
    publisherList: [],
    frequencyCapEnabled: Boolean(data.frequencyCapPerDay ?? data.frequency_cap_per_day),
    frequencyCapPerDay: data.frequencyCapPerDay ?? data.frequency_cap_per_day ?? undefined,
  };
};

export const getUpdatedChannelValues = (oldData, newData) => {
  const defaultDataByChannel = oldData.reduce((acc, data) => {
    const channelKey = getChannelKeyFromPayload(data);
    if (!channelKey) return acc;
    return { ...acc, [channelKey]: { audienceFactor: 1, impressions: null } };
  }, {});

  const normalizedData = (() => {
    if (!newData || typeof newData !== 'object') return {};

    const entries = Object.entries(newData);
    const hasChannelLikeKeys = entries.some(
      ([key]) => CHANNEL_ID_MAP[key] || CHANNEL_NAME_BY_ID[key] || CHANNEL_NAME_BY_ID[+key],
    );

    if (hasChannelLikeKeys) {
      return entries.reduce((acc, [key, value]) => {
        const channelKey = CHANNEL_ID_MAP[key] ? key : (CHANNEL_NAME_BY_ID[key] ?? CHANNEL_NAME_BY_ID[+key] ?? key);
        return { ...acc, [channelKey]: value };
      }, {});
    }

    if (oldData?.length === 1) {
      const channelKey = getChannelKeyFromPayload(oldData[0]);
      if (!channelKey) return {};
      return { [channelKey]: newData };
    }

    return newData;
  })();

  return { ...defaultDataByChannel, ...normalizedData };
};
